import { AssignmentTurnedIn, Delete } from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import CustomButton from 'lib/designSystem/Button';
import { cellFormat } from 'lib/designSystem/Table/cellFormat';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { calculateDays, isDateAWeekDay } from 'lib/functions/dates';
import { formatDate, formatMoney, showAlert } from 'lib/functions/utils';
import React from 'react';
import { useDispatch } from 'react-redux';

import { ResellDocument } from '../../types';

const useStyles = makeStyles(() =>
  createStyles({
    table: {
      width: '100%',
      minWidth: '1400px',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    tableCellSmall: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '20px',
      maxWidth: '300px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCell: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '80px',
      maxWidth: '150px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCellTasa: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '80px',
      maxWidth: '100px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      textAlign: 'right',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCellNames: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '100px',
      maxWidth: '120px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableContainer: {
      border: '1px solid #e8e8e8',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      overflow: 'auto',
      maxHeight: '600px',
      overflowY: 'auto',
      overflowX: 'auto',
    },
    tableBody: {
      backgroundColor: '#ffffff',
    },
    tableHead: {
      backgroundColor: '#f8f9fa !important',
      '& th': {
        backgroundColor: '#f8f9fa !important',
        color: '#495057 !important',
        fontWeight: '600 !important',
        fontSize: '13px !important',
        padding: '16px 8px !important',
        borderBottom: '2px solid #dee2e6',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
    },
    textField: {
      fontSize: '13px !important',
      '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        '& fieldset': {
          borderColor: '#e0e0e0',
        },
        '&:hover fieldset': {
          borderColor: '#1976d2',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#1976d2',
        },
      },
      '& .MuiInputBase-input': {
        padding: '8px 12px',
        fontSize: '13px',
      },
    },
    textFieldAutocomplete: {
      fontSize: '13px !important',
      minWidth: '120px !important',
      '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        '& fieldset': {
          borderColor: '#e0e0e0',
        },
        '&:hover fieldset': {
          borderColor: '#1976d2',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#1976d2',
        },
      },
      '& .MuiInputBase-input': {
        padding: '6px 12px',
        fontSize: '13px',
      },
    },
    iconButton: {
      padding: '6px',
      margin: '0 2px',
      borderRadius: '6px',
      '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.08)',
      },
    },
    deleteButton: {
      color: '#dc3545',
      '&:hover': {
        backgroundColor: 'rgba(220, 53, 69, 0.08)',
      },
    },
    assignButton: {
      color: '#28a745',
      '&:hover': {
        backgroundColor: 'rgba(40, 167, 69, 0.08)',
      },
    },
    tableRow: {
      '&:hover': {
        backgroundColor: '#f8f9fa',
        transition: 'background-color 0.2s ease',
      },
      '&:nth-of-type(even)': {
        backgroundColor: '#fafbfc',
      },
      '&:nth-of-type(even):hover': {
        backgroundColor: '#f1f3f4',
      },
    },
    amountCell: {
      fontWeight: '600',
      color: '#2c3e50',
    },
    daysCell: {
      fontWeight: '500',
      color: '#6c757d',
    },
    rateCostCell: {
      fontWeight: '600',
      color: '#28a745',
    },
    actionsCell: {
      minWidth: '80px',
      maxWidth: '100px',
      padding: '8px !important',
    },
    chip: {
      fontSize: '11px',
      height: '20px',
      borderRadius: '10px',
    },
    '@media (max-width: 1200px)': {
      tableCell: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '150px',
      },
      tableCellNames: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '100px',
      },
      tableCellTasa: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '60px',
      },
    },
  }),
);

interface ResellsTableProps {
  resells: ResellDocument[];
  setResells: (resells: ResellDocument[]) => void;
  canForcePaymentDate: boolean;
  readOnly?: boolean;
  markResellAsModified?: (resellId: string) => void;
  isNomineeView?: boolean;
  setOpenDocumentSelector: (open: boolean) => void;
  setOpenDocumentBatchImporter: (open: boolean) => void;
}

const ResellsTable = (props: ResellsTableProps) => {
  const {
    resells,
    setResells,
    canForcePaymentDate,
    readOnly = false,
    markResellAsModified,
    isNomineeView = false,
    setOpenDocumentSelector,
    setOpenDocumentBatchImporter,
  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const dateValidators = (date: Date, type: string, resellId: string) => {
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return false;

    if (canForcePaymentDate) {
      const days = calculateDays(
        type === 'startDate' ? date : new Date(resell.startDate),
        type === 'endDate' ? date : new Date(resell.endDate),
      );
      if (days <= 0) {
        showAlert(
          dispatch,
          'warning',
          'La fecha de vencimiento no puede ser menor a la fecha de liberación',
        );
        returnTempDateToActualDate(resellId);
        return false;
      }
      return days;
    }
    if (!isDateAWeekDay(date) && type === 'endDate') {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento seleccionada no es un día hábil.',
      );
    }
    if (date < new Date() && type === 'endDate') {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento no puede ser menor a la fecha actual',
      );
      returnTempDateToActualDate(resellId);
      return false;
    }
    const days = calculateDays(
      type === 'startDate' ? date : new Date(resell.startDate),
      type === 'endDate' ? date : new Date(resell.endDate),
    );
    if (days <= 0) {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento no puede ser menor a la fecha de liberación',
      );
      returnTempDateToActualDate(resellId);
      return false;
    }
    return days;
  };

  const returnTempDateToActualDate = (resellId: string) => {
    const newResells = resells.map((doc: any) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          tempEndDate: doc.endDate,
        };
      }
      return doc;
    });
    setResells(newResells);
  };

  const handleChangeTempDate = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    type: string,
    resellId: string,
  ) => {
    const newResells = resells.map((doc: any) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          [type]: e.target.value,
        };
      }
      return doc;
    });
    setResells(newResells);
  };

  const handleChangeDate = (value: any, type: string, resellId: string) => {
    const days = dateValidators(new Date(value), type, resellId);
    if (!days) return;
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return;
    const newResells = resells.map((doc: any) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          [type]: value,
          days,
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
  };

  const handleChangeCessionDate = (value: any, resellId: string) => {
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return;
    const newResells = resells.map((doc: any) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          cessionDate: value,
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
  };

  const getRate = (value: any) => {
    if (value === '') {
      return parseFloat('0');
    }
    if (value.endsWith('.')) {
      return parseFloat(value);
    }
    return parseFloat(value);
  };

  const handleChangeCalculatedValue = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    resellId: string,
    type: string,
  ) => {
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return;

    let valueToCalculate = 0;
    let value = e.target.value;
    if (type === 'amount') {
      value = value.split('.').join('').split('$').join('');
      if (value === '') {
        valueToCalculate = parseFloat('0');
      } else if (isNaN(parseFloat(value))) {
        return;
      } else {
        valueToCalculate = parseFloat(value);
      }
      if (valueToCalculate > resell.originalAmount)
        return showAlert(
          dispatch,
          'warning',
          'El monto a financiar no puede ser mayor al monto del documento',
        );
    } else {
      const isValidFormat = /^\d*\.?\d*$/.test(value);
      if (!isValidFormat) return;
      valueToCalculate = getRate(value);
      if (valueToCalculate > 100 || valueToCalculate < 0) return;
    }
    const newResells = resells.map((doc: any) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          amount: type === 'amount' ? value : parseFloat(doc.amount),
          rate: type === 'rate' ? value : parseFloat(doc.rate),
          advance: type === 'advance' ? value : parseFloat(doc.advance),
          arrearRate:
            type === 'arrearRate' ? value : parseFloat(doc.arrearRate),
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
  };

  const getDisplayValue = (rate: any) => {
    if (!rate || rate === '') {
      return '0';
    }
    return rate.toString();
  };

  const handleChangeOperationCostFixed = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    resellId: string,
  ) => {
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return;

    const value = e.target.value.split('.').join('').split('$').join('');
    const numValue = Number(value);
    if (isNaN(numValue)) return;
    if (numValue < 0) return;
    const newResells = resells.map((doc: any) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          operationCostFixed: numValue,
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
  };

  const assignConditionsForAllResells = (sourceId: string) => {
    const sourceIndex = resells.findIndex((r) => r.id === sourceId);
    if (sourceIndex === -1) return;

    const sourceResell = resells[sourceIndex];
    const newResells = resells.map((doc: any, i: any) => {
      // Skip rows above the source row (keep them unchanged)
      if (i <= sourceIndex) {
        return doc;
      }
      // Copy conditions from source row to all rows below
      return {
        ...doc,
        rate: sourceResell.rate,
        arrearRate: sourceResell.arrearRate,
        arrearsRate: sourceResell.arrearsRate,
        arrearTableId: sourceResell.arrearTableId,
        advance: sourceResell.advance,
        startDate: sourceResell.startDate,
        endDate: sourceResell.endDate,
        tempEndDate: sourceResell.endDate,
        operationCostFixed: sourceResell.operationCostFixed,
        operationCostVariable: sourceResell.operationCostVariable,
        fundId: sourceResell.fundId,
        fund: sourceResell.fund,
        days: sourceResell.days,
      };
    });
    setResells([
      ...newResells.map((resell: any, index: any) => ({
        ...resell,
        key: index,
      })),
    ]); // Ensure a new array reference with unique keys
  };

  const assignRateToAllResells = () => {
    if (resells.length === 0) return;

    const firstRate = resells[0].rate;
    const newResells = resells.map((doc: any, i: any) => {
      if (i === 0) {
        return doc;
      }
      return {
        ...doc,
        rate: firstRate,
      };
    });

    setResells([
      ...newResells.map((resell: any, index: any) => ({
        ...resell,
        key: index,
      })),
    ]);
  };

  const assignEndDateToAllResells = () => {
    if (resells.length === 0) return;

    const firstEndDate = resells[0].endDate;
    const firstTempEndDate = resells[0].tempEndDate;
    const firstDays = resells[0].days;

    const newResells = resells.map((doc: any, i: any) => {
      if (i === 0) {
        return doc;
      }
      return {
        ...doc,
        endDate: firstEndDate,
        tempEndDate: firstTempEndDate,
        days: firstDays,
      };
    });

    setResells([
      ...newResells.map((resell: any, index: any) => ({
        ...resell,
        key: index,
      })),
    ]);
  };

  const deleteRow = (resellId: string) => {
    const resell = resells.find((r) => r.id === resellId);
    if (!resell) return;

    const newInvoices = resells.map((doc: any) => {
      if (doc.id === resellId) {
        return {
          ...doc,
          deleted: true,
        };
      }
      return doc;
    });
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
    setResells(newInvoices);
  };

  return (
    <>
      <div
        style={{
          padding: '10px 0px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Facturas</Typography>
        {!isNomineeView && (
          <div style={{ display: 'flex', gap: 10 }}>
            <CustomButton
              onClick={() => setOpenDocumentBatchImporter(true)}
              color="secondary"
            >
              Importar
            </CustomButton>
            <CustomButton
              onClick={() => setOpenDocumentSelector(true)}
              color="primary"
            >
              Agregar
            </CustomButton>
          </div>
        )}
      </div>
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              {!readOnly && (
                <TableCell className={classes.actionsCell}></TableCell>
              )}
              <TableCell className={classes.tableCell}>Folio</TableCell>
              <TableCell className={classes.tableCellNames}>Cliente</TableCell>
              <TableCell className={classes.tableCellNames}>Deudor</TableCell>
              <TableCell className={classes.tableCell}>Monto Doc.</TableCell>
              <TableCell className={classes.tableCell}>Tipo</TableCell>
              <TableCell className={classes.tableCell}>Solicitud</TableCell>
              <TableCell className={classes.tableCell}>Liberación</TableCell>
              <TableCell className={classes.tableCell}>Monto</TableCell>
              <TableCell className={classes.tableCell}>Anticipo</TableCell>
              <TableCell className={classes.tableCellTasa}>
                <LightTooltip
                  placement="top"
                  title={
                    <Typography style={{ padding: 8 }}>
                      Hacer clic para asignar la tasa del primer elemento a
                      todos
                    </Typography>
                  }
                >
                  <Box
                    onClick={assignRateToAllResells}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      '&:hover': {
                        color: '#1976d2',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Tasa (%)
                  </Box>
                </LightTooltip>
              </TableCell>
              <TableCell className={classes.tableCell}>Tasa mora(%)</TableCell>
              <TableCell className={classes.tableCell}>Comisión</TableCell>
              <TableCell className={classes.tableCell}>
                Fecha cesión obtenida
              </TableCell>
              <TableCell className={classes.tableCell}>
                <LightTooltip
                  placement="top"
                  title={
                    <Typography style={{ padding: 8 }}>
                      Hacer clic para asignar la fecha de vencimiento del primer
                      elemento a todos
                    </Typography>
                  }
                >
                  <Box
                    onClick={assignEndDateToAllResells}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      '&:hover': {
                        color: '#1976d2',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Fecha vencimiento
                  </Box>
                </LightTooltip>
              </TableCell>
              <TableCell className={classes.tableCell}>
                Monto financiado
              </TableCell>
              <TableCell className={classes.tableCell}>Días</TableCell>
              <TableCell className={classes.tableCell}>
                Diferencia precio
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {resells
              .filter((row: any) => !row.deleted)
              .map((row: any) => {
                return (
                  <TableRow key={row.id} className={classes.tableRow}>
                    {!readOnly && (
                      <TableCell className={classes.actionsCell}>
                        <Box display="flex" justifyContent="start">
                          <IconButton
                            className={classes.iconButton}
                            onClick={() => deleteRow(row.id)}
                          >
                            <Delete className={classes.deleteButton} />
                          </IconButton>
                          <LightTooltip
                            placement="right"
                            title={
                              <Typography style={{ padding: 12 }}>
                                Asignar mismas condiciones a todas las filas de
                                abajo
                              </Typography>
                            }
                          >
                            <IconButton
                              className={classes.iconButton}
                              onClick={() =>
                                assignConditionsForAllResells(row.id)
                              }
                            >
                              <AssignmentTurnedIn
                                className={classes.assignButton}
                              />
                            </IconButton>
                          </LightTooltip>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell className={classes.tableCell}>
                      {row.folio}
                    </TableCell>
                    <TableCell className={classes.tableCellNames}>
                      {row.businessName}
                    </TableCell>
                    <TableCell className={classes.tableCellNames}>
                      {row.stakeholderName}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Box className={classes.amountCell} textAlign="right">
                        <Chip
                          label={formatMoney(row.originalAmount)}
                          className={classes.chip}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell className={classes.tableCellSmall}>
                      {row.requestType ? row.requestType : 'N/A'}
                    </TableCell>
                    <TableCell className={classes.tableCellTasa}>
                      {row.requestId
                        ? cellFormat(row.requestId, 'numeric-id')
                        : 'N/A'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {formatDate(row.paymentDate)}
                    </TableCell>
                    <TableCell className={classes.tableCellTasa}>
                      <TextField
                        value={formatMoney(row.amount)}
                        onChange={(e) =>
                          handleChangeCalculatedValue(e, row.id, 'amount')
                        }
                        onFocus={(e) => {
                          if (e.target.value === '0') {
                            e.target.select();
                          }
                        }}
                        InputProps={{ classes: { input: classes.textField } }}
                        disabled={readOnly}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCellTasa}>
                      <TextField
                        value={getDisplayValue(row.advance)}
                        onChange={(e) =>
                          handleChangeCalculatedValue(e, row.id, 'advance')
                        }
                        onFocus={(e) => {
                          if (e.target.value === '0') {
                            e.target.select();
                          }
                        }}
                        InputProps={{ classes: { input: classes.textField } }}
                        disabled={readOnly}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCellTasa}>
                      <TextField
                        value={getDisplayValue(row.rate)}
                        onChange={(e) =>
                          handleChangeCalculatedValue(e, row.id, 'rate')
                        }
                        onFocus={(e) => {
                          if (e.target.value === '0') {
                            e.target.select();
                          }
                        }}
                        InputProps={{ classes: { input: classes.textField } }}
                        disabled={readOnly}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCellTasa}>
                      <TextField
                        value={getDisplayValue(row.arrearRate)}
                        onChange={(e) =>
                          handleChangeCalculatedValue(e, row.id, 'arrearRate')
                        }
                        onFocus={(e) => {
                          if (e.target.value === '0') {
                            e.target.select();
                          }
                        }}
                        InputProps={{ classes: { input: classes.textField } }}
                        disabled={readOnly}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <TextField
                        value={formatMoney(row.operationCostFixed)}
                        onChange={(e) =>
                          handleChangeOperationCostFixed(e, row.id)
                        }
                        InputProps={{ classes: { input: classes.textField } }}
                        disabled={readOnly}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <TextField
                        value={row.cessionDate}
                        type="date"
                        onChange={(e) =>
                          handleChangeCessionDate(e.target.value, row.id)
                        }
                        InputProps={{ classes: { input: classes.textField } }}
                        disabled={readOnly}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <TextField
                        value={row.tempEndDate}
                        type="date"
                        onChange={(e) =>
                          handleChangeTempDate(e, 'tempEndDate', row.id)
                        }
                        onBlur={() =>
                          handleChangeDate(row.tempEndDate, 'endDate', row.id)
                        }
                        InputProps={{ classes: { input: classes.textField } }}
                        disabled={readOnly}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Box className={classes.daysCell}>
                        <Chip
                          label={`${formatMoney((row.amount * row.advance) / 100) || 0}`}
                          className={classes.chip}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Box className={classes.daysCell}>
                        <Chip
                          label={`${row.days || 0} días`}
                          className={classes.chip}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Box className={classes.rateCostCell} textAlign="right">
                        <Chip
                          label={formatMoney(row.rateCost)}
                          className={classes.chip}
                          color="success"
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default ResellsTable;
